import React, { useState } from 'react';
import {
  AdminHeader,
  AdminSidebar,
  ButtonPro,
  Typography,
  EmploymentHistorySidebar,
  VehicleListEditor
} from 'components';
import { RootState, useAppSelector } from 'store';
import { useParams, useNavigate } from 'react-router';
import './index.scss';
import moment from 'moment';
import { format } from 'date-fns';
import { UserRoles } from 'models/enums';

export const ViewEmployee = () => {
  const navigate = useNavigate();
  const { id: selectedEmployeeId } = useParams();
  const [isHistorySidebarOpen, setIsHistorySidebarOpen] = useState(false);
  const isAdmin = useAppSelector(
    (state: RootState) =>
      state.auth.user.authData?.data?.userRole === UserRoles.ADMIN
  );

  const formatDate = (date: string) =>
    date ? moment.utc(date).format('DD/MM/YYYY') : '';

  const selectedEmployee = useAppSelector((state: RootState) => {
    return selectedEmployeeId
      ? state.admin.employeeData.find(
          (employee) => employee.userRoleId === selectedEmployeeId
        )
      : null;
  });

  const allEmployees = useAppSelector((state: RootState) => {
    return state.admin.employeeData;
  });
  const { employees } = useAppSelector((state: RootState) => state.masterData);

  // Helper function to get employee name by ID
  const getEmployeeNameById = (employeeId: string) => {
    const employee = allEmployees.find(
      (emp) => emp.employeeInfoId === employeeId
    );
    return (
      employee?.employeeNameWithInitials ||
      employee?.employeeNickName ||
      'Unknown'
    );
  };

  return (
    <>
      <AdminHeader />
      <div className="dashboard-wrapper">
        <AdminSidebar />
        <div className="manage-employe-container">
          <div className="manage-employe-title view-employee">
            <Typography
              text={`${
                selectedEmployee?.employeeNameWithInitials ||
                selectedEmployee?.upn
              } - ${selectedEmployee?.role}`}
              type="base_regular"
              weight="bold"
            />

            <div className="employee-actions">
              <ButtonPro
                type="white"
                htmlType="button"
                label="Employment History"
                onClick={() => setIsHistorySidebarOpen(true)}
              />
              <ButtonPro
                type="primary"
                htmlType="button"
                label="Edit details"
                onClick={() => {
                  navigate(
                    `/admin/edit-employee/${selectedEmployee?.userRoleId}`
                  );
                }}
              />
            </div>
          </div>

          <div className="manage-employe-form employee-info">
            <div className="registration-step-wrapper">
              <div className="step-number">1</div>
              <div className="step-info">
                <Typography
                  text="Personal Information"
                  type="base_regular"
                  weight="bold"
                />
                <Typography
                  text="Enter basic details about the employee"
                  type="small_medium"
                  color="gray"
                />
              </div>
            </div>

            <div className="employee-info-view">
              <div className="employee-info-row">
                <div className="employee-info-title">Full name</div>
                <div className="employee-info-value">
                  {selectedEmployee?.employeeFullName}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Name with initials</div>
                <div className="employee-info-value">
                  {selectedEmployee?.employeeNameWithInitials}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Nick name</div>
                <div className="employee-info-value">
                  {selectedEmployee?.employeeNickName}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Active status</div>
                <div className="employee-info-value">
                  {selectedEmployee?.isActive ? 'Active' : 'Inactive'}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Gender</div>
                <div className="employee-info-value capitalize">
                  {selectedEmployee?.gender}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Marital status</div>
                <div className="employee-info-value capitalize">
                  {selectedEmployee?.maritalStatus}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Religion</div>
                <div className="employee-info-value">
                  {selectedEmployee?.religion}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Nationality</div>
                <div className="employee-info-value">
                  {selectedEmployee?.nationality}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Date of birth</div>
                <div className="employee-info-value">
                  {selectedEmployee?.dateOfBirth
                    ? formatDate(selectedEmployee?.dateOfBirth)
                    : ''}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">
                  NIC or Passport number
                </div>
                <div className="employee-info-value">
                  {selectedEmployee?.nic}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Passport number</div>
                <div className="employee-info-value">
                  {selectedEmployee?.passportNumber || 'N/A'}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Mobile number</div>
                <div className="employee-info-value">
                  {selectedEmployee?.mobileNumber}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">
                  Residential phone number
                </div>
                <div className="employee-info-value">
                  {selectedEmployee?.residentialPhoneNumber}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Email address</div>
                <div className="employee-info-value">
                  {selectedEmployee?.upn}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">
                  Personal email address
                </div>
                <div className="employee-info-value">
                  {selectedEmployee?.personalEmail}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">House number</div>
                <div className="employee-info-value">
                  {selectedEmployee?.houseNumber}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Address</div>
                <div className="employee-info-value">
                  {selectedEmployee?.address}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">District</div>
                <div className="employee-info-value">
                  {selectedEmployee?.district}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Province</div>
                <div className="employee-info-value">
                  {selectedEmployee?.province}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Temporary address</div>
                <div className="employee-info-value">
                  {selectedEmployee?.temporyAddress || 'N/A'}
                </div>
              </div>
            </div>

            <div className="registration-step-wrapper">
              <div className="step-number">2</div>
              <div className="step-info">
                <Typography
                  text="Employment Information"
                  type="base_regular"
                  weight="bold"
                />
                <Typography
                  text="Enter basic details about the employee"
                  type="small_medium"
                  color="gray"
                />
              </div>
            </div>

            <div className="employee-info-view">
              <div className="employee-info-row">
                <div className="employee-info-title">Employee ID</div>
                <div className="employee-info-value">
                  {selectedEmployee?.epfNumber}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Designation</div>
                <div className="employee-info-value">
                  {selectedEmployee?.designationName}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Joined date</div>
                <div className="employee-info-value">
                  {selectedEmployee?.joinedDate
                    ? formatDate(selectedEmployee?.joinedDate)
                    : ''}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">
                  Team Lead or Reporting Manager
                </div>
                <div className="employee-info-value">
                  {employees?.map((emp) =>
                    emp.employeeId === selectedEmployee?.nameOfTheTeamLead
                      ? emp.employeeName
                      : null
                  )}
                  {/* {selectedEmployee?.nameOfTheTeamLead || 'N/A'} */}
                </div>
              </div>

              <div className="employee-info-row">
                <div className="employee-info-title">Project History</div>
                <div
                  className="employee-info-value"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}
                >
                  {selectedEmployee?.employeeProjects &&
                  selectedEmployee.employeeProjects.length > 0 ? (
                    // Sort projects by start date (newest first) and separate current from historical
                    [...selectedEmployee.employeeProjects]
                      .sort((a, b) => {
                        // Current projects first, then by start date (newest first)
                        if (a.isCurrent && !b.isCurrent) return -1;
                        if (!a.isCurrent && b.isCurrent) return 1;
                        return (
                          new Date(b.startDate || '').getTime() -
                          new Date(a.startDate || '').getTime()
                        );
                      })
                      .map((project, index) => (
                        <div
                          key={project.id}
                          style={{
                            padding: '8px',
                            backgroundColor: project.isCurrent
                              ? '#f0f8ff'
                              : '#f9f9f9',
                            border: project.isCurrent
                              ? '2px solid #2b2b57'
                              : '1px solid #ddd',
                            borderRadius: '4px',
                            position: 'relative'
                          }}
                        >
                          {project.isCurrent && (
                            <span
                              style={{
                                position: 'absolute',
                                top: '-8px',
                                right: '8px',
                                backgroundColor: '#2b2b57',
                                color: '#eee',
                                fontSize: '12px',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontWeight: 'bold'
                              }}
                            >
                              CURRENT
                            </span>
                          )}
                          <div
                            style={{
                              fontWeight: 'bold',
                              marginBottom: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            {project.projects.name}
                            <span
                              style={{
                                fontSize: '14px',
                                color: '#666',
                                fontWeight: 'normal'
                              }}
                            >
                              {project.roleInProject}
                            </span>
                          </div>

                          <div
                            style={{
                              fontSize: '14px',
                              color: '#666',
                              marginBottom: '2px'
                            }}
                          >
                            <strong>Duration:</strong>{' '}
                            {project.startDate
                              ? format(project.startDate, 'MMMM yyyy')
                              : 'N/A'}{' '}
                            to{' '}
                            {project.endDate
                              ? format(project.endDate, 'MMMM yyyy')
                              : 'Present'}
                          </div>
                          {/* <div
                            style={{
                              fontSize: '14px',
                              color: '#666',
                              marginBottom: '2px'
                            }}
                          >
                            <strong>Allocation:</strong>{' '}
                            {project.allocationPercentage}%
                          </div> */}
                          {project.notes && (
                            <div
                              style={{
                                fontSize: '12px',
                                color: '#888',
                                fontStyle: 'italic',
                                marginTop: '4px'
                              }}
                            >
                              {project.notes}
                            </div>
                          )}
                        </div>
                      ))
                  ) : (
                    <div
                      style={{
                        padding: '16px',
                        textAlign: 'center',
                        color: '#666',
                        backgroundColor: '#f9f9f9',
                        borderRadius: '4px'
                      }}
                    >
                      No projects assigned
                    </div>
                  )}
                </div>
              </div>

              <div className="employee-info-row">
                <div className="employee-info-title">Current Project</div>
                <div className="employee-info-value">
                  {(() => {
                    const currentProject =
                      selectedEmployee?.employeeProjects?.find(
                        (p) => p.isCurrent
                      );
                    return currentProject
                      ? `${currentProject.projects.name} (${currentProject.roleInProject})`
                      : 'No active project assignment';
                  })()}
                </div>
              </div>

              <div className="employee-info-row">
                <div className="employee-info-title">ProjectManager</div>
                <div className="employee-info-value">
                  {employees?.map((emp) =>
                    emp.employeeId === selectedEmployee?.projectManager
                      ? emp.employeeName
                      : null
                  )}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Tech Stack</div>
                <div className="employee-info-value">
                  {selectedEmployee?.techStack}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Confirmation due date</div>
                <div className="employee-info-value">
                  {selectedEmployee?.confirmationDate
                    ? formatDate(selectedEmployee?.confirmationDate)
                    : ''}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Status</div>
                <div className="employee-info-value">
                  {selectedEmployee?.status}
                </div>
              </div>

              {selectedEmployee?.status === 'Terminated' ? (
                <>
                  <div className="employee-info-row">
                    <div className="employee-info-title">Termination date</div>
                    <div className="employee-info-value">
                      {selectedEmployee?.terminationDate
                        ? formatDate(selectedEmployee?.terminationDate)
                        : null}
                    </div>
                  </div>
                  <div className="employee-info-row">
                    <div className="employee-info-title">Terminated by</div>
                    <div className="employee-info-value">
                      {selectedEmployee?.terminatedBy}
                    </div>
                  </div>
                  <div className="employee-info-row">
                    <div className="employee-info-title">Terminated reason</div>
                    <div className="employee-info-value">
                      {selectedEmployee?.terminatedReason}
                    </div>
                  </div>
                </>
              ) : null}

              <div className="employee-info-row">
                <div className="employee-info-title">Annual leave count</div>
                <div className="employee-info-value">
                  {selectedEmployee?.annualLeaveCount}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Casual leave count</div>
                <div className="employee-info-value">
                  {selectedEmployee?.casualLeaveCount}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Medical leave count</div>
                <div className="employee-info-value">
                  {selectedEmployee?.medicalLeaveCount}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Lieu leave count</div>
                <div className="employee-info-value">
                  {selectedEmployee?.liueLeaveCount}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">No pay leave count</div>
                <div className="employee-info-value">
                  {selectedEmployee?.noPayLeaveCount}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">Remarks</div>
                <div className="employee-info-value">
                  {selectedEmployee?.remarks || 'N/A'}
                </div>
              </div>
            </div>

            <div className="registration-step-wrapper">
              <div className="step-number">3</div>
              <div className="step-info">
                <Typography
                  text="Emergency Contact Details"
                  type="base_regular"
                  weight="bold"
                />
                <Typography
                  text="Enter basic details about the employee"
                  type="small_medium"
                  color="gray"
                />
              </div>
            </div>

            <div className="employee-info-view">
              <div className="employee-info-row">
                <div className="employee-info-title">
                  Emergency contact person
                </div>
                <div className="employee-info-value">
                  {selectedEmployee?.emergencyContactPerson}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">
                  Emergency contact person number
                </div>
                <div className="employee-info-value">
                  {selectedEmployee?.emergencyContactPersonPhoneNumber}
                </div>
              </div>
              <div className="employee-info-row">
                <div className="employee-info-title">
                  Emergency contact person relationship
                </div>
                <div className="employee-info-value">
                  {selectedEmployee?.emergencyContactPersonRelationship}
                </div>
              </div>
            </div>

            {/* Mentor Assignment Section */}
            {selectedEmployee?.mentorAssignment && (
              <>
                <div className="registration-step-wrapper">
                  <div className="step-number">4</div>
                  <div className="step-info">
                    <Typography
                      text="Mentor Assignment"
                      type="base_regular"
                      weight="bold"
                    />
                    <Typography
                      text="Assigned mentors for this employee"
                      type="small_medium"
                      color="gray"
                    />
                  </div>
                </div>

                <div className="employee-info-view">
                  <div className="employee-info-row">
                    <div className="employee-info-title">Mentor 1</div>
                    <div className="employee-info-value">
                      {getEmployeeNameById(
                        selectedEmployee.mentorAssignment.mentor01Id
                      )}
                    </div>
                  </div>
                  <div className="employee-info-row">
                    <div className="employee-info-title">Mentor 2</div>
                    <div className="employee-info-value">
                      {getEmployeeNameById(
                        selectedEmployee.mentorAssignment.mentor02Id
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {isAdmin && selectedEmployee?.employeeInfoId && (
              <>
                <div className="registration-step-wrapper">
                  <div className="step-number">
                    {selectedEmployee.mentorAssignment ? 5 : 4}
                  </div>
                  <div className="step-info">
                    <Typography
                      text="Vehicles"
                      type="base_regular"
                      weight="bold"
                    />
                    <Typography
                      text="Vehicles registered to this employee"
                      type="small_medium"
                      color="gray"
                    />
                  </div>
                </div>

                <VehicleListEditor
                  target={{ employeeInfoId: selectedEmployee.employeeInfoId }}
                  emptyText="No vehicles added. Use Edit details to add one."
                  readOnly
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Employment History Sidebar */}
      <EmploymentHistorySidebar
        isOpen={isHistorySidebarOpen}
        onClose={() => setIsHistorySidebarOpen(false)}
        employeeData={selectedEmployee || null}
      />
    </>
  );
};
